import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SecurityIssue, Agent } from "../types/security";
import {
  mockSecurityIssues,
  mockMonthlyChartData,
  mockSeverityStats,
  mockChartMetadata,
  getDayLabelsForRange,
  DailyIssue,
  mockStatusTrendData,
  mockAgents,
  mockComplianceScores,
} from "../data/mockSecurityIssues";
import { SecurityIssuesList } from "../components/security/SecurityIssuesList";
import { IssueStatusTrendChart } from "../components/security/IssueStatusTrendChart";
import { AgentsList } from "../components/security/AgentsList";
import { AgentDetail } from "../components/security/AgentDetail";
import { ComplianceOverview } from "../components/security/ComplianceOverview";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Shield,
  TrendingUp,
  TrendingDown,
  Filter,
  MoreVertical,
  RefreshCw,
  Download,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface ChartHoverData {
  day: number;
  date: string;
  issues: DailyIssue[];
  totalCount: number;
  x: number;
  y: number;
}

export const SecurityDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [criticalHover, setCriticalHover] = useState<ChartHoverData | null>(null);
  const [highHover, setHighHover] = useState<ChartHoverData | null>(null);
  const [dateRange, setDateRange] = useState<"7" | "30" | "90">("30");
  const [statusDateRange, setStatusDateRange] = useState<"7" | "30" | "90">("30");

  const handleExportCSV = () => {
    const csvData = mockSecurityIssues
      .map(
        (issue) =>
          `"${issue.id}","${issue.type}","${issue.title}","${issue.severity}","${issue.status}","${issue.riskScore}","${issue.timestamp}"`
      )
      .join("\n");
    const header = '"ID","Type","Title","Severity","Status","Risk Score","Timestamp"\n';
    const blob = new Blob([header + csvData], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `security-issues-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const filteredChartData = mockMonthlyChartData.slice(0, parseInt(dateRange));
  const maxDays = parseInt(dateRange);
  const dayLabels = getDayLabelsForRange(maxDays, mockChartMetadata.month);

  const totalCritical = filteredChartData.reduce((sum, day) => sum + day.criticalCount, 0);
  const totalHigh = filteredChartData.reduce((sum, day) => sum + day.highCount, 0);
  const totalMedium = Math.round(mockSeverityStats.medium.count * (maxDays / 30));
  const totalLow = Math.round(mockSeverityStats.low.count * (maxDays / 30));

  const handleIssueClick = (issue: SecurityIssue) => {
    navigate(`/issues/${issue.id}`);
  };

  if (selectedAgent) {
    const agentIssues = mockSecurityIssues.filter((issue) =>
      selectedAgent.issueIds.includes(issue.id)
    );
    return (
      <div className="container mx-auto p-6">
        <AgentDetail
          agent={selectedAgent}
          issues={agentIssues}
          onClose={() => setSelectedAgent(null)}
        />
      </div>
    );
  }

  return (
    <div className="container-md mx-auto p-6 space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="md:col-span-1">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-base font-medium">Open Issues</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">Current versus a month...</p>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => window.location.reload()}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Refresh Data
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportCSV}>
                  <Download className="mr-2 h-4 w-4" />
                  Export as CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between border-l-4 border-red-500 pl-4">
              <div>
                <div className="text-3xl font-bold">{totalCritical}</div>
                <div className="text-sm text-muted-foreground">Critical issues</div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-1 justify-end">
                  {mockSeverityStats.critical.trend === "up" ? (
                    <TrendingUp className="h-4 w-4 text-red-600" />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-green-600" />
                  )}
                  <span
                    className={`text-xs font-medium ${
                      mockSeverityStats.critical.trend === "up" ? "text-red-600" : "text-green-600"
                    }`}
                  >
                    {mockSeverityStats.critical.trendPercentage}%
                  </span>
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {Math.round(mockSeverityStats.critical.openCount * (maxDays / 30))} open
                </div>
              </div>
            </div>

            {/* High */}
            <div className="flex items-center justify-between border-l-4 border-orange-500 pl-4">
              <div>
                <div className="text-3xl font-bold">{totalHigh}</div>
                <div className="text-sm text-muted-foreground">High issues</div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-1 justify-end">
                  {mockSeverityStats.high.trend === "up" ? (
                    <TrendingUp className="h-4 w-4 text-red-600" />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-green-600" />
                  )}
                  <span
                    className={`text-xs font-medium ${
                      mockSeverityStats.high.trend === "up" ? "text-red-600" : "text-green-600"
                    }`}
                  >
                    {mockSeverityStats.high.trendPercentage}%
                  </span>
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {Math.round(mockSeverityStats.high.openCount * (maxDays / 30))} open
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between border-l-4 border-yellow-500 pl-4">
              <div>
                <div className="text-3xl font-bold">{totalMedium}</div>
                <div className="text-sm text-muted-foreground">Medium issues</div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-1 justify-end">
                  {mockSeverityStats.medium.trend === "up" ? (
                    <TrendingUp className="h-4 w-4 text-red-600" />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-green-600" />
                  )}
                  <span
                    className={`text-xs font-medium ${
                      mockSeverityStats.medium.trend === "up" ? "text-red-600" : "text-green-600"
                    }`}
                  >
                    {mockSeverityStats.medium.trendPercentage}%
                  </span>
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {Math.round(mockSeverityStats.medium.openCount * (maxDays / 30))} open
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between border-l-4 border-gray-400 pl-4">
              <div>
                <div className="text-3xl font-bold">{totalLow}</div>
                <div className="text-sm text-muted-foreground">Low issues</div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-1 justify-end">
                  {mockSeverityStats.low.trend === "up" ? (
                    <TrendingUp className="h-4 w-4 text-red-600" />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-green-600" />
                  )}
                  <span
                    className={`text-xs font-medium ${
                      mockSeverityStats.low.trend === "up" ? "text-red-600" : "text-green-600"
                    }`}
                  >
                    {mockSeverityStats.low.trendPercentage}%
                  </span>
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {Math.round(mockSeverityStats.low.openCount * (maxDays / 30))} open
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="md:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-base font-medium">Issues by severity</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">Last {dateRange} days</p>
            </div>
            <div className="flex gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <Filter className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Filter Chart</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setDateRange("7")}>
                    {dateRange === "7" && "✓ "}Last 7 days
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setDateRange("30")}>
                    {dateRange === "30" && "✓ "}Last 30 days
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setDateRange("90")}>
                    {dateRange === "90" && "✓ "}Last 90 days
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Chart Options</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => window.location.reload()}>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Refresh Data
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleExportCSV}>
                    <Download className="mr-2 h-4 w-4" />
                    Export as CSV
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Critical</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-red-600 flex items-center">↑10%</span>
                    <span className="text-sm font-bold">221</span>
                  </div>
                </div>
                <div className="h-32 bg-gradient-to-b from-red-100 to-red-50 rounded relative overflow-hidden">
                  <svg className="w-full h-full" viewBox="0 0 900 100" preserveAspectRatio="none">
                    <path
                      d={filteredChartData
                        .map((point, idx) => {
                          const x = (idx / (maxDays - 1)) * 900;
                          const y = 80 - (point.criticalCount / 25) * 60; // Scale based on max 25
                          return `${idx === 0 ? "M" : "L"} ${x},${y}`;
                        })
                        .join(" ")}
                      fill="none"
                      stroke="#dc2626"
                      strokeWidth="2"
                    />
                    {filteredChartData.map((point, idx) => {
                      const x = (idx / (maxDays - 1)) * 900;
                      const y = 80 - (point.criticalCount / 25) * 60;
                      return (
                        <circle
                          key={idx}
                          cx={x}
                          cy={y}
                          r="20"
                          fill="transparent"
                          className="cursor-pointer hover:fill-red-200/30"
                          onMouseEnter={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setCriticalHover({
                              day: point.day,
                              date: point.date,
                              issues: point.criticalIssues,
                              totalCount: point.criticalCount,
                              x: rect.left + rect.width / 2,
                              y: rect.top,
                            });
                          }}
                          onMouseLeave={() => setCriticalHover(null)}
                        />
                      );
                    })}
                  </svg>
                  {criticalHover && (
                    <div
                      className="fixed z-50 bg-white px-3 py-2 rounded shadow-lg border text-xs max-w-xs"
                      style={{
                        left: `${criticalHover.x}px`,
                        top: `${criticalHover.y - 10}px`,
                        transform: "translate(-50%, -100%)",
                      }}
                    >
                      <div className="font-semibold mb-1">{criticalHover.date}</div>
                      <div className="text-red-600 font-medium mb-2">
                        {criticalHover.totalCount} Critical Issues
                      </div>
                      <div className="space-y-1">
                        {criticalHover.issues.map((issue, idx) => (
                          <div key={idx} className="flex justify-between gap-4">
                            <span className="text-muted-foreground">{issue.type}:</span>
                            <span className="font-medium">{issue.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex justify-between text-xs text-muted-foreground mt-1 px-1">
                  {dayLabels.map((label, idx) => (
                    <span key={idx}>
                      {label.day} {label.month}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">High</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-red-600 flex items-center">↑6%</span>
                    <span className="text-sm font-bold">602</span>
                  </div>
                </div>
                <div className="h-32 bg-gradient-to-b from-orange-100 to-orange-50 rounded relative overflow-hidden">
                  <svg className="w-full h-full" viewBox="0 0 900 100" preserveAspectRatio="none">
                    <path
                      d={filteredChartData
                        .map((point, idx) => {
                          const x = (idx / (maxDays - 1)) * 900;
                          const y = 80 - (point.highCount / 40) * 60; // Scale based on max 40
                          return `${idx === 0 ? "M" : "L"} ${x},${y}`;
                        })
                        .join(" ")}
                      fill="none"
                      stroke="#f97316"
                      strokeWidth="2"
                    />
                    {filteredChartData.map((point, idx) => {
                      const x = (idx / (maxDays - 1)) * 900;
                      const y = 80 - (point.highCount / 40) * 60;
                      return (
                        <circle
                          key={idx}
                          cx={x}
                          cy={y}
                          r="20"
                          fill="transparent"
                          className="cursor-pointer hover:fill-orange-200/30"
                          onMouseEnter={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setHighHover({
                              day: point.day,
                              date: point.date,
                              issues: point.highIssues,
                              totalCount: point.highCount,
                              x: rect.left + rect.width / 2,
                              y: rect.top,
                            });
                          }}
                          onMouseLeave={() => setHighHover(null)}
                        />
                      );
                    })}
                  </svg>
                  {highHover && (
                    <div
                      className="fixed z-50 bg-white px-3 py-2 rounded shadow-lg border text-xs max-w-xs"
                      style={{
                        left: `${highHover.x}px`,
                        top: `${highHover.y - 10}px`,
                        transform: "translate(-50%, -100%)",
                      }}
                    >
                      <div className="font-semibold mb-1">{highHover.date}</div>
                      <div className="text-orange-600 font-medium mb-2">
                        {highHover.totalCount} High Severity Issues
                      </div>
                      <div className="space-y-1">
                        {highHover.issues.map((issue, idx) => (
                          <div key={idx} className="flex justify-between gap-4">
                            <span className="text-muted-foreground">{issue.type}:</span>
                            <span className="font-medium">{issue.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex justify-between text-xs text-muted-foreground mt-1 px-1">
                  {dayLabels.map((label, idx) => (
                    <span key={idx}>
                      {label.day} {label.month}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-5">
        <Card className="md:col-span-3">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-base font-medium">
              Security Issues ({mockSecurityIssues.length})
            </CardTitle>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7">
                  <MoreVertical className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Options</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => window.location.reload()}>
                  <RefreshCw className="mr-2 h-3.5 w-3.5" />
                  Refresh Data
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportCSV}>
                  <Download className="mr-2 h-3.5 w-3.5" />
                  Export as CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </CardHeader>
          <CardContent className="pt-0">
            {mockSecurityIssues.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No issues found matching your filters.</p>
              </div>
            ) : (
              <div className="max-h-[400px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                <SecurityIssuesList issues={mockSecurityIssues} onIssueClick={handleIssueClick} />
              </div>
            )}
          </CardContent>
        </Card>

        <div className="md:col-span-2">
          <IssueStatusTrendChart
            data={mockStatusTrendData.slice(0, parseInt(statusDateRange))}
            dateRange={statusDateRange}
            onDateRangeChange={setStatusDateRange}
          />
        </div>
      </div>

      {/* Agents Section */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-base font-medium">AI Agents ({mockAgents.length})</CardTitle>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7">
                  <MoreVertical className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Options</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => window.location.reload()}>
                  <RefreshCw className="mr-2 h-3.5 w-3.5" />
                  Refresh Data
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="max-h-[400px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              <AgentsList agents={mockAgents} onAgentClick={setSelectedAgent} />
            </div>
          </CardContent>
        </Card>

        <ComplianceOverview scores={mockComplianceScores} />
      </div>
    </div>
  );
};
